import { useDataSentryContext } from '../context/DataSentryContext'
import { useTableFilters } from '../hooks/useTableFilters'
import { useDataSentryEventsByClassification } from 'api/dataSentry/queries/useDataSentryEventsByClassification'
import { useRef, useMemo } from 'react'
import {
  DataGrid,
  EmptyState,
  IconDictionary,
  Spinner,
  Chip,
  LabelChipVariant,
  TableCell,
  theme,
} from '@ketch-com/deck'
import { SearchTextInput } from 'components/searchTextInput/SearchTextInput'
import { EventsByClassificationCount, ClassificationType } from '@ketch-com/windlass/dist/dataSentry/v1/events/events'
import { LinearProgress, Typography, Tooltip, Box } from '@mui/material'
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { EmptyValueRenderer } from 'components/EmptyValueRenderer'
import { AssetsTabTableLabelChip } from 'pages/dataSystems/DataSystem/assetsTab/components/AssetsTabTableLabelChip'

export const DataTypesCollected: React.FC = () => {
  const { searchQuery, setSearchQuery, paginationModel, setPaginationModel } = useTableFilters(10)

  const {
    selectedTimeRange,
    selectedPropertyCode,
    eventsSummary,
    setDataClassificationDetails,
    hasPropertyAndScanTimeRange,
  } = useDataSentryContext()
  const { data: eventsByClassificationsList, isLoading } = useDataSentryEventsByClassification({
    params: {
      'classification-type': ClassificationType.DATA_TYPE,
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedPropertyCode,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      query: searchQuery,
    },
    enabled: hasPropertyAndScanTimeRange,
  })

  const totalCount = eventsSummary?.total || 0
  const rowCountRef = useRef(eventsByClassificationsList?.total || 0)
  const rowCount = useMemo(() => {
    if (eventsByClassificationsList?.total !== undefined) {
      rowCountRef.current = eventsByClassificationsList.total
    }
    return rowCountRef.current
  }, [eventsByClassificationsList])

  const columns: GridColDef<EventsByClassificationCount>[] = [
    {
      align: 'left',
      field: 'classificationType',
      headerName: 'Data Type',
      flex: 2,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => {
        return (
          <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
            {params.colDef.headerName}
          </Typography>
        )
      },
      renderCell: ({ row }: GridRenderCellParams<EventsByClassificationCount>) => {
        return (
          <TableCell>
            <Box
              onClick={() => {
                let type: ClassificationType = ClassificationType.DATA_TYPE

                setDataClassificationDetails?.({
                  type,
                  name: row.classification?.name || '',
                  code: row.classification?.code || '',
                })
              }}
            >
              <AssetsTabTableLabelChip
                title={row.classification?.name || row.classification?.code}
                variant={LabelChipVariant.classification}
                hideConfidence
                hideIcon
                supportMultiLineTitle
                isViewOnly
              />
            </Box>
          </TableCell>
        )
      },
    },
    {
      align: 'left',
      field: 'precentage Graph',
      headerName: '',
      flex: 1,
      minWidth: 80,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="smallLabel" sx={{}} color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<EventsByClassificationCount>) => {
        const percentage = Math.round(((row?.count || 0) / totalCount) * 100)
        return (
          <TableCell width="100%">
            {totalCount ? (
              <LinearProgress
                sx={{
                  borderRadius: 24,
                  height: 8,
                  flexGrow: '1',
                }}
                variant="determinate"
                value={percentage}
                style={{ width: '90%' }}
              />
            ) : (
              <EmptyValueRenderer />
            )}
          </TableCell>
        )
      },
    },
    {
      align: 'right',
      field: 'collectionEvents',
      headerName: 'Collection Events',
      flex: 1,
      minWidth: 70,
      headerAlign: 'right',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography
          variant="smallLabel"
          sx={{ textWrap: 'wrap' }}
          color={theme.palette.Text.Secondary}
          textAlign="right"
        >
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<EventsByClassificationCount>) => {
        return (
          <TableCell>
            <Typography variant="body">{row.count}</Typography>
          </TableCell>
        )
      },
    },
    {
      align: 'right',
      field: 'systemsCollection',
      headerName: 'Systems Collecting',
      flex: 1,
      minWidth: 70,
      headerAlign: 'right',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography
          variant="smallLabel"
          sx={{ textWrap: 'wrap' }}
          color={theme.palette.Text.Secondary}
          textAlign="right"
        >
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<EventsByClassificationCount>) => {
        return (
          <TableCell>
            {row.dataSystemCount ? (
              <Tooltip
                placement="bottom"
                title={
                  <Box display="flex" flexDirection="column" gap={0.5} maxHeight={120} overflow="auto">
                    {row?.dataSystems?.map(dataSystem => (
                      <Typography key={dataSystem?.code}>{dataSystem?.name || dataSystem?.code}</Typography>
                    ))}
                  </Box>
                }
              >
                <Box>
                  <Chip clickable label={row.dataSystemCount} size="small" />
                </Box>
              </Tooltip>
            ) : (
              <Typography>Unknown</Typography>
            )}
          </TableCell>
        )
      },
    },
  ]

  const displayDataGridEmptyState = Boolean(!searchQuery && !eventsByClassificationsList?.classifications)
  const displayDataGrid = Boolean(searchQuery || eventsByClassificationsList?.classifications)

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1}
      sx={{
        backgroundColor: theme.palette.Common.Container,
      }}
      borderRadius={3}
      px={2.5}
      py={2}
      flex={1}
    >
      <Box display="flex" justifyContent="space-between" gap={3}>
        <Typography variant="h4">Data Types Collected</Typography>
        {displayDataGrid ? (
          <SearchTextInput
            onChange={nextValue => setSearchQuery(nextValue)}
            value={searchQuery}
            showClearButton
            minWidth="176px"
            sx={{
              width: '176px',
              '& .MuiInputBase-input': { maxWidth: '112px', minWidth: '112px' },
            }}
          />
        ) : null}
      </Box>
      <Box>
        {isLoading && !displayDataGrid ? (
          <Box display="flex" alignItems="center" justifyContent="center" height={620}>
            <Spinner size="32px" thickness={2.5} />
          </Box>
        ) : null}
        {!isLoading && displayDataGridEmptyState ? (
          <EmptyState
            size="small"
            colorTheme={theme.palette.Text.Secondary}
            iconName={IconDictionary.FCollection}
            title="No Data"
            subTitle="Review your settings and connections, or wait for the scan to finish processing."
          />
        ) : null}
        {displayDataGrid ? (
          <Box height={380}>
            <DataGrid
              disableRowHoverStates
              disableRowSelectionOnClick
              disableColumnMenu
              disableColumnPinning
              disableColumnReorder
              disableColumnResize
              disableChildrenSorting={false}
              disableBorder
              getRowHeight={() => 'auto'}
              getRowId={row => row.classification.code}
              rowCount={rowCount}
              rows={eventsByClassificationsList.classifications || []}
              columns={columns}
              pagination
              pageSizeOptions={[10]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              loading={isLoading}
              paginationMode="server"
              noRowsOverlayProps={{ buttonTitle: '' }}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}
